import { useMemo } from "react";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import type { IFareData, IFlightFares, TripCategory } from "@overrides/types";
import { tripCategoryToTripType } from "@overrides/utilities";
import { useFareKeys } from "@tenant/flights";
import clsx from "clsx";
import defaultStyles from "./FlightFareGrid.module.scss";
import { useRbcSlot } from "@/components/slots";

export interface TFlightFareGrid {
  className?: string;
  fares: IFlightFares;
  fareShelfFilter?: number[];
  tripType: TripCategory;
  tagsLimit?: number;
  onFareGridClick: (fare: IFareData) => void;
}

export const FlightFareGrid = ({
  fares,
  tripType,
  tagsLimit = 2,
  onFareGridClick,
  fareShelfFilter,
  className,
}: TFlightFareGrid) => {
  const styles = useFlightStyles(
    CoreFlightsComponent.FlightFareGrid,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreFlightsComponent.FlightFareGrid);

  const RbcSlot = useRbcSlot();
  const fareKeys = useFareKeys();
  const grid = useMemo(() => {
    return fareKeys.map((fareKey, index) => {
      const fare: IFareData | undefined = fares[fareKey];
      return fare != null ? (
        <RbcSlot.FlightFareGridItem
          key={`fare-grid-${fare.fareId}`}
          fare={fare}
          tripType={tripCategoryToTripType(tripType)}
          tagsLimit={tagsLimit}
          onFareGridClick={onFareGridClick}
          isFilteredOut={!(fareShelfFilter?.includes(fare.fareShelf) ?? false)}
          numberOfFares={fare.numberOfFares}
        />
      ) : (
        <div key={`no-fare-${index}`} className={cn("empty-fare")}></div>
      );
    });
  }, [
    RbcSlot,
    cn,
    fareKeys,
    fareShelfFilter,
    fares,
    onFareGridClick,
    tagsLimit,
    tripType,
  ]);
  return <ul className={clsx(block, className)}>{grid}</ul>;
};
